import type { AppRecord, LiveStatus } from '../../lib/types';
import type { CompassLocale } from '../i18n';

export type CompassView = 'home' | 'gallery' | 'library' | 'demos' | 'about';
export type AppSort = 'latest' | 'name' | 'score';

const VALID_VIEWS: CompassView[] = ['home', 'gallery', 'library', 'demos', 'about'];

export function normalizeCompassView(input: string | null | undefined): CompassView {
  if (!input) return 'home';
  return (VALID_VIEWS.includes(input as CompassView) ? input : 'home') as CompassView;
}

export function buildCompassViewHref(view: CompassView, locale: CompassLocale): string {
  const url = new URL('/', 'http://compass.local');
  url.searchParams.set('lang', locale);
  if (view !== 'home') {
    url.searchParams.set('view', view);
  }
  return `${url.pathname}${url.search}${url.hash}`;
}

export function deriveAppCategory(app: AppRecord): string {
  const [prefix] = app.appId.split('-');
  if (!prefix) return 'Studio';
  return prefix
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map((token) => token[0]!.toUpperCase() + token.slice(1))
    .join(' ');
}

export function deriveAppTags(app: AppRecord): string[] {
  const tags = new Set<string>();
  tags.add(deriveAppCategory(app));
  if (app.preview.url) tags.add('Preview');
  if (app.deploy.present) tags.add('Deploy');
  if (app.axle.resources.length > 0) tags.add('API');
  if (app.screenshots.length > 0) tags.add('Visual');
  if (app.artifacts.some((artifact) => artifact.key.includes('scribe'))) tags.add('Docs');
  return Array.from(tags).slice(0, 3);
}

export function isDemoAvailable(app: AppRecord): boolean {
  return Boolean(app.preview.url || app.deploy.present || app.appClass === 'live-ready');
}

export function resolveDemoHref(app: AppRecord, runtimeStatus: LiveStatus): string | undefined {
  if (app.preview.url) return app.preview.url;
  if (runtimeStatus === 'healthy') return `/runtime/apps/${app.appId}/`;
  return undefined;
}

export function promptExcerpt(prompt: string, maxLength = 220): string {
  const normalized = prompt.replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength).trimEnd()}...`;
}

export function formatRunDate(sourceRunId: string, locale: CompassLocale): string | undefined {
  const match = sourceRunId.match(/(20\d{2})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/);
  if (!match) return undefined;
  const [, year, month, day] = match;
  if (locale === 'zh-CN') {
    return `${year}-${month}-${day}`;
  }
  return `${year}-${month}-${day}`;
}

export function sortApps(apps: AppRecord[], sort: AppSort): AppRecord[] {
  const copy = [...apps];
  if (sort === 'score') {
    return copy.sort((a, b) => (b.statuses.harnessScore ?? -1) - (a.statuses.harnessScore ?? -1));
  }
  if (sort === 'name') {
    return copy.sort((a, b) => a.name.localeCompare(b.name));
  }
  return copy.sort((a, b) => b.sourceRunId.localeCompare(a.sourceRunId));
}

export function relatedApps(apps: AppRecord[], currentAppId: string, count = 3): AppRecord[] {
  return apps.filter((app) => app.appId !== currentAppId).slice(0, count);
}
